<template>
	<!-- `novalidate`: `createRoute` is this form's one validator and reports
	     through the alert above the button; the browser's interactive
	     validation must not run over the id inputs, which hold their rows'
	     zero until touched. -->
	<form novalidate @submit.prevent="createRoute">
		<p class="text-body-secondary small mb-3">
			A Route is created in <em>draft</em> with one technician and the
			operational window it exists for, holding either existing draft
			assignments, new work authored here, or neither (§8 Rule 1). It cannot be
			dispatched until it holds at least one Assignment: an empty Route is a
			plan container, filled later from the Route itself.
		</p>

		<!-- The panel is a narrow column inside a wide viewport, so the grid has
		     to fit the column rather than the page: the two timestamps keep a
		     pair of columns the `datetime-local` control can still be read in,
		     and one column of anything else sits full width. -->
		<div class="row g-2">
			<div class="col-12">
				<label class="form-label small mb-1" for="routeTechnician">
					Technician
				</label>
				<select
					id="routeTechnician"
					v-model.number="technicianId"
					class="form-select form-select-sm"
				>
					<option :value="0" disabled>Choose a technician…</option>
					<option v-for="user in technicians" :key="user.id" :value="user.id">
						{{ user.display_name }}
					</option>
				</select>
			</div>
			<div class="col-6">
				<label class="form-label small mb-1" for="routeWindowStart">
					Window start
				</label>
				<input
					id="routeWindowStart"
					v-model="windowStart"
					type="datetime-local"
					class="form-control form-control-sm"
				/>
			</div>
			<div class="col-6">
				<label class="form-label small mb-1" for="routeWindowEnd">
					Window end
				</label>
				<input
					id="routeWindowEnd"
					v-model="windowEnd"
					type="datetime-local"
					class="form-control form-control-sm"
				/>
			</div>
			<div class="col-12">
				<label class="form-label small mb-1" for="routeAssignments">
					Existing assignments to start with
				</label>
				<select
					id="routeAssignments"
					v-model="assignmentIds"
					multiple
					size="4"
					class="form-select form-select-sm"
				>
					<option
						v-for="assignment in assignable"
						:key="assignment.id"
						:value="assignment.id"
					>
						{{ assignmentLabel(assignment) }}
					</option>
				</select>
				<div class="form-text">
					That technician's draft work that no Route holds. The Route may start
					empty — work can be added later from the Route.
				</div>
			</div>
		</div>

		<!-- The composing door: the Route and new work are one write, so an
		     operator planning a day states the assignments it implies here rather
		     than authoring each one first and returning to route it. Each item
		     inherits the Route's technician and is born draft. -->
		<div class="mt-3">
			<div class="d-flex align-items-center justify-content-between gap-2">
				<span class="form-label small mb-0">New work</span>
				<button
					type="button"
					class="btn btn-outline-secondary btn-sm"
					@click="addRow"
				>
					<i class="bi bi-plus-lg me-1"></i>Add work
				</button>
			</div>
			<p class="form-text mb-2">
				Assignments authored with this Route, created draft and appended to the
				membership after the existing assignments above, in the order shown.
			</p>

			<p
				v-if="rows.length === 0"
				class="text-body-secondary small fst-italic mb-2"
			>
				No new work — this Route starts with only the assignments selected
				above.
			</p>

			<div
				v-for="(row, index) in rows"
				:key="row.key"
				class="border rounded p-2 mb-2"
			>
				<div class="d-flex align-items-center justify-content-between mb-2">
					<span class="small fw-semibold">Work item {{ index + 1 }}</span>
					<button
						type="button"
						class="btn btn-outline-danger btn-sm"
						@click="removeRow(index)"
					>
						Remove
					</button>
				</div>

				<div class="row g-2">
					<div class="col-6">
						<label
							class="form-label small mb-1"
							:for="`routeWorkKind-${row.key}`"
						>
							Kind
						</label>
						<select
							:id="`routeWorkKind-${row.key}`"
							v-model="row.kind"
							class="form-select form-select-sm"
						>
							<option value="" disabled>Choose a kind…</option>
							<option
								v-for="option in KIND_OPTIONS"
								:key="option.value"
								:value="option.value"
							>
								{{ option.label }}
							</option>
						</select>
					</div>
					<div class="col-12">
						<label
							class="form-label small mb-1"
							:for="`routeWorkInstructions-${row.key}`"
						>
							Instructions
							<span
								v-if="row.kind === 'custom'"
								class="badge bg-warning text-dark ms-1"
							>
								Required for a custom assignment
							</span>
						</label>
						<textarea
							:id="`routeWorkInstructions-${row.key}`"
							v-model="row.instructions"
							class="form-control form-control-sm"
							:class="{
								'border-warning border-2': row.kind === 'custom',
							}"
							rows="2"
							placeholder="What the technician is asked to do, and anything they need to know on arrival."
						></textarea>
					</div>
				</div>

				<!-- A Trap Configuration is the instruction a `set_trap` carries,
				     and only that kind may name one. The district's configurations
				     are the same read AssignmentForm takes; a retired family is not
				     offered for new work. -->
				<div v-if="row.kind === 'set_trap'" class="row g-2 mt-2">
					<div class="col-12">
						<label
							class="form-label small mb-1"
							:for="`routeWorkTrapConfiguration-${row.key}`"
						>
							Trap configuration
						</label>
						<select
							:id="`routeWorkTrapConfiguration-${row.key}`"
							v-model.number="row.trapConfigurationId"
							class="form-select form-select-sm"
						>
							<option :value="0">None stated</option>
							<option
								v-for="configuration in liveConfigurations"
								:key="configuration.id"
								:value="configuration.id"
							>
								{{ configuration.name }} (v{{ configuration.version }})
							</option>
						</select>
					</div>
				</div>

				<fieldset class="mt-2">
					<legend class="form-label small fs-6 mb-1">
						Target — exactly one
					</legend>
					<div
						v-for="mode in TARGET_MODES"
						:key="mode.value"
						class="form-check form-check-inline"
					>
						<input
							:id="`routeWorkTarget-${row.key}-${mode.value}`"
							v-model="row.targetMode"
							class="form-check-input"
							type="radio"
							:value="mode.value"
						/>
						<label
							class="form-check-label small"
							:for="`routeWorkTarget-${row.key}-${mode.value}`"
						>
							{{ mode.label }}
						</label>
					</div>

					<div class="mt-2">
						<select
							v-if="row.targetMode === 'site'"
							v-model.number="row.targetSiteId"
							class="form-select form-select-sm"
						>
							<option :value="0" disabled>Choose a site…</option>
							<option v-for="site in sites" :key="site.id" :value="site.id">
								{{ siteLabel(site) }}
							</option>
						</select>

						<template v-else-if="row.targetMode === 'feature'">
							<div class="input-group input-group-sm">
								<span class="input-group-text">Feature #</span>
								<input
									v-model.number="row.targetFeatureId"
									type="number"
									min="1"
									class="form-control"
									@change="lookupFeature(row)"
								/>
								<button
									type="button"
									class="btn btn-outline-secondary"
									:disabled="row.featureChecking"
									@click="lookupFeature(row)"
								>
									Check
								</button>
							</div>
							<p class="form-text mb-0">
								{{
									row.featureNote ||
									"The office has no Feature picker; state the id and it is checked against the Features ledger."
								}}
							</p>
						</template>

						<!-- The district's Facilitators are readable on the device pull
						     only, so the id is stated rather than chosen. -->
						<div
							v-else-if="row.targetMode === 'facilitator'"
							class="input-group input-group-sm"
						>
							<span class="input-group-text">Facilitator #</span>
							<input
								v-model.number="row.targetFacilitatorId"
								type="number"
								min="1"
								class="form-control"
							/>
						</div>

						<!-- The GeoJSON is stated, not drawn: the workbench mounts no
						     map here. It is the same string the API takes
						     (`target_geometry`). -->
						<template v-else>
							<!-- A carried Work row's target is its own footprint: stated,
							     not typed, because it is the place the district drew.
							     Switching the target mode away and back brings it back. -->
							<p v-if="row.workRowId !== null" class="form-text mb-0">
								The H3 footprint of Work row #{{ row.workRowId }} — the place
								this work is for.
							</p>
							<template v-else>
								<textarea
									v-model="row.targetGeometry"
									class="form-control form-control-sm font-monospace"
									rows="2"
									placeholder='{"type": "Point", "coordinates": [-122.4, 37.8]}'
								></textarea>
								<p class="form-text mb-0">
									A GeoJSON geometry in WGS 84 — the shape the completion gate
									measures the recorded evidence against.
								</p>
							</template>
						</template>
					</div>
				</fieldset>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger py-2 small mt-3 mb-0">
			{{ error }}
		</div>

		<div class="d-flex justify-content-end gap-2 mt-3">
			<button
				type="button"
				class="btn btn-outline-secondary btn-sm"
				@click="emit('cancel')"
			>
				Cancel
			</button>
			<ButtonLoading
				type="submit"
				text="Create route"
				icon="bi bi-signpost-split"
				variant="primary"
				:loading="saving"
				:disabled="saving || technicianId <= 0"
			/>
		</div>
	</form>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import type {
	AssignmentKind,
	AssignmentSpec,
	MapReviewFeatureRow,
	MapReviewQueryEnvelope,
	MapReviewSiteRow,
	RouteCreate,
	SyncAssignment,
	SyncRoute,
	TrapConfigurationResult,
	User,
} from "@/type/api";

import type { RouteCarry } from "./model";

import {
	defaultWindow,
	describeError,
	kindLabel,
	KIND_OPTIONS,
	siteLabel,
	targetLabel,
	wireInstant,
} from "./wire";
import { resolveWorkTargets } from "./work-targets";

const props = defineProps<{
	users: User[];
	assignments: SyncAssignment[];
	sites: MapReviewSiteRow[];
	/** What the Route tool carries in when a Details pane door opened it. The
	 * seed is bumped per press; zero is a pane opened from its own icon, which
	 * carries nothing. */
	carry: RouteCarry;
}>();

const emit = defineEmits<{
	notify: [title: string, message: string];
	created: [route: SyncRoute];
	cancel: [];
}>();

type TargetMode = "site" | "feature" | "facilitator" | "geometry";

interface WorkRow {
	key: number;
	/** Empty until the operator chooses: the kind is the work's identity, so it
	 * has no default the way the standalone form's `inspect` is a default. */
	kind: AssignmentKind | "";
	instructions: string;
	trapConfigurationId: number;
	targetMode: TargetMode;
	targetSiteId: number;
	targetFeatureId: number;
	targetFacilitatorId: number;
	targetGeometry: string;
	featureNote: string;
	featureChecking: boolean;
	/** The Work row this item was carried in from, or null for one the operator
	 * added by hand. Its presence is what makes the target the row's own
	 * footprint rather than a typed geometry. */
	workRowId: number | null;
	/** The Work row's provenance, so the assignment records the Lead it came
	 * from or converts the Work Order it came from. */
	leadId: number | null;
	workOrderId: number | null;
}

const TARGET_MODES: { value: TargetMode; label: string }[] = [
	{ value: "site", label: "Site" },
	{ value: "feature", label: "Feature" },
	{ value: "facilitator", label: "Facilitator" },
	{ value: "geometry", label: "Drawn geometry" },
];

const initialWindow = defaultWindow();
const technicianId = ref(0);
const windowStart = ref(initialWindow.start);
const windowEnd = ref(initialWindow.end);
const assignmentIds = ref<number[]>([]);
const rows = ref<WorkRow[]>([]);
const trapConfigurations = ref<TrapConfigurationResult[]>([]);
const saving = ref(false);
const error = ref("");

let nextRowKey = 1;

const technicians = computed(() =>
	props.users.filter((user) => user.is_active),
);

/** The technician's draft work no Route holds — the exact set the create door
 * accepts as `assignment_ids`. */
const assignable = computed(() =>
	props.assignments.filter(
		(row) =>
			row.technicianId === technicianId.value &&
			row.state === "draft" &&
			row.routeId === null,
	),
);

const liveConfigurations = computed(() =>
	trapConfigurations.value.filter((configuration) => !configuration.retired),
);

function assignmentLabel(assignment: SyncAssignment): string {
	return `#${assignment.id} — ${kindLabel(assignment.kind)} · ${targetLabel(assignment, props.sites)}`;
}

function blankRow(): WorkRow {
	return {
		key: nextRowKey++,
		kind: "",
		instructions: "",
		trapConfigurationId: 0,
		targetMode: "site",
		targetSiteId: 0,
		targetFeatureId: 0,
		targetFacilitatorId: 0,
		targetGeometry: "",
		featureNote: "",
		featureChecking: false,
		workRowId: null,
		leadId: null,
		workOrderId: null,
	};
}

function addRow() {
	rows.value.push(blankRow());
}

// Carrying a Details pane selection in. Work rows become composed work items —
// the row's kind, its title as the instructions, its whole footprint as the
// target — and draft assignments become the Route's starting membership, which
// also states the technician they belong to. A row already carried in is not
// added twice, so the door can be pressed again after widening the selection and
// only the new rows arrive. Rows whose footprint cannot be read are reported
// rather than carried without their place.
let carried = 0;

watch(
	() => props.carry,
	async (carry) => {
		if (carry.seed === 0 || carry.seed === carried) return;
		carried = carry.seed;
		if (carry.technicianId > 0) technicianId.value = carry.technicianId;
		if (carry.assignmentIds.length > 0) {
			assignmentIds.value = [
				...new Set([...assignmentIds.value, ...carry.assignmentIds]),
			];
		}
		if (carry.work.length === 0) return;
		try {
			const resolved = await resolveWorkTargets(carry.work);
			if (carried !== carry.seed) return;
			const already = new Set(
				rows.value
					.map((row) => row.workRowId)
					.filter((id): id is number => id !== null),
			);
			for (const { row, geometry } of resolved.targets) {
				if (already.has(row.id)) continue;
				rows.value.push({
					...blankRow(),
					kind: row.origin === "lead" ? "inspect" : "custom",
					instructions: row.title,
					targetMode: "geometry",
					targetGeometry: JSON.stringify(geometry),
					workRowId: row.id,
					leadId: row.origin === "lead" ? row.id : null,
					workOrderId: row.origin === "work_order" ? row.id : null,
				});
			}
			if (resolved.refusals.length > 0) {
				emit(
					"notify",
					"Some Work rows not carried",
					resolved.refusals
						.map(
							(refusal) =>
								`#${refusal.row.id} ${refusal.row.title} ${refusal.reason}.`,
						)
						.join(" "),
				);
			}
		} catch (e: unknown) {
			emit("notify", "Work rows not carried", describeError(e));
		}
	},
	{ immediate: true },
);

function removeRow(index: number) {
	rows.value.splice(index, 1);
}

// The same read the standalone form's Trap Configuration select takes; a district
// with none simply offers "None stated".
onMounted(async () => {
	try {
		trapConfigurations.value = await apiClient.JSONGet<
			TrapConfigurationResult[]
		>("/api/trap-configuration");
	} catch (e: unknown) {
		emit("notify", "Trap configurations not loaded", describeError(e));
	}
});

// The Feature target is stated by id because the office has no Feature list
// read: the Features ledger's QUERY answers one row for an id, so the check is
// the operator's confirmation that the id names work in this district.
async function lookupFeature(row: WorkRow) {
	row.featureNote = "";
	if (!row.targetFeatureId) return;
	row.featureChecking = true;
	try {
		const envelope = await apiClient.JSONQuery<
			MapReviewQueryEnvelope<MapReviewFeatureRow>
		>("/api/feature", {
			limit: 1,
			filter: [
				{ property: "id", operator: "equal", value: row.targetFeatureId },
			],
		});
		const feature = envelope.items[0];
		row.featureNote = feature
			? `Feature #${feature.id} — ${feature.type || "unclassified"} at ${feature.site}.`
			: `No feature #${row.targetFeatureId} in this district.`;
	} catch (e: unknown) {
		emit("notify", "Feature check failed", describeError(e));
	} finally {
		row.featureChecking = false;
	}
}

type SpecResult = { spec: AssignmentSpec } | { problem: string };

/** One row as the spec the door takes, or what stops it, named by row. The
 * Route's technician is inherited and `state` is left off: the door creates
 * every spec draft, and a spec that stated a different technician would be
 * refused. */
function specForRow(row: WorkRow, index: number): SpecResult {
	const name = `Work item ${index + 1}`;
	if (row.kind === "") return { problem: `${name}: choose a kind.` };
	const instructions = row.instructions.trim();
	if (row.kind === "custom" && instructions === "") {
		return {
			problem: `${name}: a custom assignment needs instructions describing the work.`,
		};
	}

	const spec: AssignmentSpec = { kind: row.kind };
	if (instructions !== "") spec.instructions = instructions;
	if (row.kind === "set_trap" && row.trapConfigurationId > 0) {
		spec.trap_configuration_id = row.trapConfigurationId;
	}
	// A row carried in from the Work queue names its origin, so the door links
	// the Lead (or converts the open Work Order) in the same write.
	if (row.leadId !== null) spec.lead_id = row.leadId;
	if (row.workOrderId !== null) spec.work_order_id = row.workOrderId;

	switch (row.targetMode) {
		case "site":
			if (row.targetSiteId <= 0) {
				return { problem: `${name}: choose the site it is for.` };
			}
			spec.target_site_id = row.targetSiteId;
			break;
		case "feature":
			if (row.targetFeatureId <= 0) {
				return { problem: `${name}: state the feature it is for.` };
			}
			spec.target_feature_id = row.targetFeatureId;
			break;
		case "facilitator":
			if (row.targetFacilitatorId <= 0) {
				return { problem: `${name}: state the facilitator it is for.` };
			}
			spec.target_facilitator_id = row.targetFacilitatorId;
			break;
		case "geometry": {
			const geometry = row.targetGeometry.trim();
			if (geometry === "") {
				return { problem: `${name}: draw or paste the target geometry.` };
			}
			try {
				JSON.parse(geometry);
			} catch {
				return { problem: `${name}: the target geometry is not valid JSON.` };
			}
			spec.target_geometry = geometry;
			break;
		}
	}
	return { spec };
}

/** Every row's spec in display order, or null after naming the first row that
 * cannot be sent. Zero rows is a legal answer: an empty draft Route. */
function collectSpecs(): AssignmentSpec[] | null {
	const specs: AssignmentSpec[] = [];
	for (const [index, row] of rows.value.entries()) {
		const result = specForRow(row, index);
		if ("problem" in result) {
			error.value = result.problem;
			return null;
		}
		specs.push(result.spec);
	}
	return specs;
}

async function createRoute() {
	error.value = "";
	if (technicianId.value <= 0) {
		error.value = "Choose the technician the Route is for.";
		return;
	}
	const start = wireInstant(windowStart.value);
	const end = wireInstant(windowEnd.value);
	if (start === null || end === null) {
		error.value = "State both ends of the Route's window.";
		return;
	}
	if (new Date(end).getTime() <= new Date(start).getTime()) {
		error.value = "The Route's window must end after it starts.";
		return;
	}
	const assignments = collectSpecs();
	if (assignments === null) return;

	saving.value = true;
	const body: RouteCreate = {
		technician_id: technicianId.value,
		window_start: start,
		window_end: end,
		assignment_ids: [...assignmentIds.value],
		assignments,
	};
	try {
		const created = await apiClient.JSONPost<SyncRoute>("/api/route", body);
		const membership =
			created.members.length === 0
				? "is an empty draft"
				: `holds ${created.members.length} assignment(s)`;
		emit("notify", "Route created", `Route #${created.id} ${membership}.`);
		emit("created", created);
	} catch (e: unknown) {
		error.value = describeError(e);
		emit("notify", "Route not created", error.value);
	} finally {
		saving.value = false;
	}
}
</script>
